import React from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  Policy as PolicyIcon,
  ChevronRight as ChevronRightIcon,
  Code as CodeIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { staggerContainer, staggerItem } from '../../animations/variants';

// Format ISO 8601 duration to a more readable format
const formatDuration = (duration) => {
  if (!duration) return 'N/A';

  if (duration.startsWith('PT')) {
    const hours = duration.match(/(\d+)H/);
    const minutes = duration.match(/(\d+)M/);

    let result = '';
    if (hours) result += `${hours[1]} hour${hours[1] !== '1' ? 's' : ''} `;
    if (minutes) result += `${minutes[1]} minute${minutes[1] !== '1' ? 's' : ''}`;

    return result.trim() || 'N/A';
  }

  return duration;
};

// Helper function to count non-empty inline policies
const countInlinePolicies = (inlinePolicies) => {
  if (!inlinePolicies) return 0;
  return Object.values(inlinePolicies).filter(policy => policy && policy.trim() !== '').length;
};

const PermissionSetList = ({ permissionSets, onPermissionSetClick, loading }) => {
  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
      }}>
        <CircularProgress sx={{ color: '#27AE60' }} />
      </Box>
    );
  }

  if (!permissionSets || permissionSets.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        gap: 2,
        py: 6,
      }}>
        <Avatar
          sx={{
            width: 64,
            height: 64,
            backgroundColor: '#F0F9F4',
          }}
        >
          <SecurityIcon sx={{ fontSize: 32, color: '#27AE60' }} />
        </Avatar>
        <Typography
          sx={{
            color: '#253E66',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            fontSize: '1.1rem',
            textAlign: 'center',
          }}
        >
          No permission sets found
        </Typography>
        <Typography
          sx={{
            color: '#666666',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 500,
            fontSize: '0.875rem',
            textAlign: 'center',
            maxWidth: 300,
          }}
        >
          Create your first permission set to get started
        </Typography>
      </Box>
    );
  }

  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate">
    <List sx={{ p: 0 }}>
      {permissionSets.map((permissionSet, index) => {
        // Use count fields from slim response, fallback to computing from arrays for backward compatibility
        const managedPoliciesCount = permissionSet.managed_policies_count ??
          (permissionSet.managedPolicies || permissionSet.managed_policies || []).length;
        const inlinePoliciesCount = permissionSet.inline_policies_count ??
          countInlinePolicies(permissionSet.inlinePolicies || permissionSet.inline_policies);
        const customerManagedPoliciesCount = permissionSet.customer_managed_policies_count ??
          (permissionSet.customerManagedPolicies || permissionSet.customer_managed_policies || []).length;

        return (
          <motion.div key={permissionSet.id} variants={staggerItem}>
          <ListItem
            disablePadding
            sx={{
              borderBottom: index < permissionSets.length - 1 ? '1px solid #E5E5E5' : 'none',
            }}
          >
            <ListItemButton
              onClick={() => onPermissionSetClick(permissionSet.id)}
              sx={{
                py: 2,
                px: 3,
                '&:hover': {
                  backgroundColor: '#F9FBFD',
                },
              }}
            >
              <ListItemAvatar>
                <Avatar
                  sx={{
                    backgroundColor: '#F0F9F4',
                    width: 44,
                    height: 44,
                  }}
                >
                  <SecurityIcon sx={{ color: '#27AE60', fontSize: 22 }} />
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: '#253E66',
                      mb: 0.5,
                    }}
                  >
                    {permissionSet.name}
                  </Typography>
                }
                secondary={
                  <Box>
                    {permissionSet.description && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                        <PolicyIcon sx={{ fontSize: 14, color: '#999999' }} />
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#666666',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        >
                          {permissionSet.description}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ScheduleIcon sx={{ fontSize: 14, color: '#999999' }} />
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#666666',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        >
                          {formatDuration(permissionSet.sessionDuration || permissionSet.session_duration)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SecurityIcon sx={{ fontSize: 14, color: '#999999' }} />
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#666666',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        >
                          {managedPoliciesCount} managed {managedPoliciesCount !== 1 ? 'policies' : 'policy'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CodeIcon sx={{ fontSize: 14, color: '#999999' }} />
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#666666',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        >
                          {inlinePoliciesCount} inline {inlinePoliciesCount !== 1 ? 'policies' : 'policy'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ArticleIcon sx={{ fontSize: 14, color: '#999999' }} />
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#666666',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        >
                          {customerManagedPoliciesCount} customer managed {customerManagedPoliciesCount !== 1 ? 'policies' : 'policy'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                }
              />

              <ChevronRightIcon sx={{ color: '#BDBDBD', fontSize: 24 }} />
            </ListItemButton>
          </ListItem>
          </motion.div>
        );
      })}
    </List>
    </motion.div>
  );
};

export default PermissionSetList;
